import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from "axios";
import { get, isEmpty } from "lodash";
import configDetails from "../constants/config";

// Create a token provider function that can be set by the auth context
let authAccessor: () => Promise<any> = async () => null;

export const setTokenProvider = (fn: () => Promise<any>): void => {
  authAccessor = fn;
};

const redirectToLogin = async (): Promise<null | void> => {
  const user = await authAccessor();
  if (!user) {
    return null;
  }
  if (typeof user?.logout === "function") {
    user.logout().catch((error: Error) => {
      console.error("Logout failed", error);
    });
  }
};

// Helper function to get valid token
const getAccessToken = async (): Promise<string | null> => {
  const user = await authAccessor();

  if (!user) {
    const token = sessionStorage.getItem(
      `oidc.user:${configDetails.KEYCLOAK.apiHost}${configDetails.KEYCLOAK.apiPath}/realms/${configDetails.realm}/:${configDetails.realm}`
    );
    if (token) {
      const obj = JSON.parse(token);
      return obj.access_token;
    }
    return null;
  }

  // If within 30s of expiry, attempt silent renew
  const now = Math.floor(Date.now() / 1000);
  if (user.expires_at && user.expires_at - now < 30 && typeof user.renew === "function") {
    try {
      const renewed = await user.renew();
      return renewed?.access_token || null;
    } catch {
      return null;
    }
  }
  return user.access_token || null;
};

const fetcher = async (
  method: string,
  endpoint: string,
  data: any = null,
  authenticated: boolean = true,
  baseUrlPath: string = ""
): Promise<any> => {
  const url =
    baseUrlPath === ""
      ? configDetails.ADMIN.apiHost + configDetails.ADMIN.apiPath
      : (configDetails as any)[baseUrlPath].apiHost + (configDetails as any)[baseUrlPath].apiPath;

  let config: AxiosRequestConfig = { method: method as Method, url: `${url}${endpoint}`, responseType: "json" };

  if (authenticated) {
    const token = await getAccessToken();

    if (!token) {
      console.log("No valid token found, redirecting to login...");
      redirectToLogin();
      return Promise.reject(new Error("No valid authentication token"));
    }

    if (isEmpty(baseUrlPath)) {
      config.headers = {
        Authorization: `Bearer ${token}`,
        "x-companyId": localStorage.getItem("company-id") || "",
        "x-companyName": encodeURI(localStorage.getItem("company-name") || ""),
      };
      if (sessionStorage.getItem("x-jobId")) {
        config.headers["x-jobId"] = encodeURI(sessionStorage.getItem("x-jobId") || "");
      }
    } else {
      config.headers = { Authorization: `Bearer ${token}` };
    }
  }

  if (data) {
    config.data = data;
  }

  return axios(config)
    .then((response: AxiosResponse) => {
      return method === "get" ? response : { response, getUrl: url };
    })
    .catch((errorResponse: AxiosError | any) => {
      const errorDetail = errorResponse.response ?? errorResponse;
      const reloadCall = errorDetail?.config?.url?.split("/");
      const status = errorDetail?.status;
      const data = errorDetail?.data;
      const statusText = get(errorDetail, "statusText");
      let errorMessage = data?.message ?? statusText;

      if (errorMessage === undefined) {
        errorMessage = errorResponse.message;
      }

      if (status === 401) {
        console.log("Unauthorized access detected.");
        window.dispatchEvent(new CustomEvent("unauthorized"));
        // Return a never-settling promise so the rejection doesn't bubble up
        // as an unhandled runtime error — the modal handles the UX response.
        return new Promise(() => {});
      }

      if (status !== 400) {
        sessionStorage.setItem("errorItem", "true");
        sessionStorage.setItem("status", String(status));
        sessionStorage.setItem("errorMsg", errorMessage);
      }

      if (reloadCall?.length >= 7 && reloadCall[6] === "organisationsettings" && status === 400) {
        sessionStorage.setItem("reload", "true");
      }

      const error: any = new Error(errorMessage);
      error.status = status;
      console.error(error);

      return Promise.resolve({ response: errorMessage });
    });
};

export default fetcher;
